"use client";

import type { Division, ReportCatalog, ReportQuery } from "@gettactic/api";
import { useAuthenticated, useDivisionsQuery } from "@gettactic/hooks";
import type { MapCanvasValue } from "@gettactic/map-builder/src/components/map/contexts/MapCanvasContext";
import { MapView } from "@gettactic/map-builder/src/MapView";
import type { ZoneShape } from "@gettactic/map-builder/src/types";
import React, { useMemo } from "react";
import { useReportQuery } from "../hooks/useReportQuery";
import { formatValue } from "../lib/formatValue";
import { MapFrame } from "./MapFrame";
import { HEAT_RAMPS, heatColor } from "./ReportMapView";

/** Divisions that carry a map of their own (a campus, a region). */
export const useCampusDivisions = (): Division[] => {
  const { divisions } = useDivisionsQuery();
  return useMemo(
    () => (divisions.data ?? []).filter((d) => !!d.map_data),
    [divisions.data]
  );
};

/** Zones on a campus map that stand for an office, keyed by office id. */
const officeZones = (map: MapCanvasValue): Map<string, ZoneShape> => {
  const out = new Map<string, ZoneShape>();
  for (const shape of map.shapes) {
    if (shape.type !== "zone") {
      continue;
    }
    const zone = shape as ZoneShape;
    if (zone.link?.type === "office" && zone.link.officeId) {
      out.set(zone.link.officeId, zone);
    }
  }
  return out;
};

/** The same question, one value per office, with nothing pinned to a floor. */
const perOffice = (query: ReportQuery): ReportQuery => ({
  ...query,
  breakdown: ["office"],
  filters: (query.filters ?? []).filter((f) => f.dimension !== "floor"),
  compare: undefined
});

interface CampusMapViewProps {
  catalog: ReportCatalog;
  division: Division;
  query: ReportQuery;
  height?: number;
  onDrill?: (dimension: string, memberId: string) => void;
}

/**
 * A division's own map (a campus, a region) with each office's zone
 * coloured by the measure. Zones are linked to offices in the map builder;
 * offices without a zone are listed underneath so nothing is silently lost.
 */
export const CampusMapView: React.FC<CampusMapViewProps> = ({
  catalog,
  division,
  query,
  height = 520,
  onDrill
}) => {
  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();
  const map = useMemo<MapCanvasValue | null>(() => {
    if (!division.map_data) {
      return null;
    }
    try {
      return JSON.parse(division.map_data) as MapCanvasValue;
    } catch {
      return null;
    }
  }, [division.map_data]);
  const zones = useMemo(() => (map ? officeZones(map) : new Map()), [map]);
  const { data: result, isLoading, isError } = useReportQuery(perOffice(query));
  const measure =
    result?.meta.measure ??
    catalog.measures.find((m) => m.key === query.measure);

  const rows = useMemo(
    () => [...(result?.rows ?? [])].sort((a, b) => b.value - a.value),
    [result]
  );
  const max = rows[0]?.value ?? 0;
  const zoneColors = useMemo(() => {
    const out: Record<string, string> = {};
    for (const row of rows) {
      const zone = zones.get(row.keys[0]);
      if (zone && measure) {
        out[zone.id] = heatColor(row.value, max, measure);
      }
    }
    return out;
  }, [rows, zones, max, measure]);

  if (!map) {
    return (
      <p className="py-16 text-center text-neutral-400 text-sm">
        {division.name} has no map yet.
      </p>
    );
  }
  if (isError) {
    return (
      <p className="py-16 text-center text-[13px] text-semantic-error-foreground">
        This question couldn't be answered per office.
      </p>
    );
  }
  if (isLoading || !result || !measure) {
    return (
      <p className="py-16 text-center text-neutral-400 text-sm">
        Loading the map…
      </p>
    );
  }

  const ramp = HEAT_RAMPS[measure.good_when];
  const unplaced = rows.filter((r) => !zones.has(r.keys[0]));

  return (
    <div className="flex flex-col gap-2">
      <MapFrame height={height} contentKey={`division-${division.id}`}>
        <MapView
          value={map}
          useSVG
          zoneColors={zoneColors}
          hideZoneLegend
          authenticatedUser={authenticatedUser}
          isPrivacyMode={false}
        />
      </MapFrame>
      <div className="flex items-center gap-3 text-[11px] text-neutral-500">
        <span>{division.name}</span>
        <span className="text-neutral-300">·</span>
        <span>
          {result.meta.from} – {result.meta.to}
        </span>
        <span className="flex-1" />
        <span>0</span>
        <span className="flex overflow-hidden rounded">
          {ramp.map((c) => (
            <span
              key={c}
              className="h-2.5 w-5"
              style={{ backgroundColor: c }}
            />
          ))}
        </span>
        <span className="tabular-nums">{formatValue(measure, max)}</span>
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-[12px]">
        {rows.map((row) => (
          <button
            type="button"
            key={row.keys[0]}
            onClick={() => onDrill?.("office", row.keys[0])}
            className="flex items-center gap-1.5 rounded-md px-1 py-0.5 hover:bg-neutral-50"
            title={zones.has(row.keys[0]) ? undefined : "Not on this map"}
          >
            <span
              className="h-2.5 w-2.5 rounded-[2px]"
              style={{
                backgroundColor: zones.has(row.keys[0])
                  ? heatColor(row.value, max, measure)
                  : "#ffffff",
                outline: zones.has(row.keys[0])
                  ? undefined
                  : "1px dashed #c7cbd1"
              }}
            />
            <span className="text-neutral-700">{row.labels[0]}</span>
            <span className="text-neutral-500 tabular-nums">
              {formatValue(measure, row.value)}
            </span>
          </button>
        ))}
        {unplaced.length > 0 ? (
          <span className="text-neutral-400">
            {unplaced.length} office{unplaced.length === 1 ? "" : "s"} not drawn
            on this map
          </span>
        ) : null}
      </div>
    </div>
  );
};
